import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError, API } from "@/lib/api";
import {
  BookOpen, Upload, Download, Search, Loader2, AlertTriangle,
  ShieldCheck, Trash2, CheckCircle2, XCircle, Filter, FileText,
  Clock, User, Info,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   Biblioteca Jurídica Colega Link · /app/biblioteca-juridica
   Colaborativa: cualquier abogado verificado sube.
   Descarga: abogados y mediadores verificados (+ admin).
   ═══════════════════════════════════════════════════════════════ */

const STATUS_META = {
  pendiente_revision: { label: "Pendiente revisión", cls: "border-amber-400/40 text-amber-300 bg-amber-400/10", icon: Clock },
  aprobado: { label: "Aprobado", cls: "border-emerald-400/40 text-emerald-300 bg-emerald-400/10", icon: CheckCircle2 },
  rechazado: { label: "Rechazado", cls: "border-red-400/40 text-red-300 bg-red-400/10", icon: XCircle },
};

const humanSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

export default function BibliotecaJuridica() {
  const { user } = useAuth();
  const [tab, setTab] = useState("directorio"); // directorio | subir | mios | admin
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ category: "", q: "" });
  const [pendingCount, setPendingCount] = useState(0);

  const canUpload = user?.role === "lawyer" && user?.verified;
  const isAdmin = user?.role === "admin";
  const canBrowse = ["lawyer", "mediator", "admin"].includes(user?.role) && (isAdmin || user?.verified);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      if (tab === "admin" && isAdmin) {
        const { data } = await api.get("/library/pending");
        setItems(data.items || []);
      } else if (tab === "mios") {
        const { data } = await api.get("/library/mine");
        setItems(data.items || []);
      } else {
        const params = new URLSearchParams();
        if (filters.category) params.set("category", filters.category);
        if (filters.q) params.set("q", filters.q);
        const { data } = await api.get(`/library/items?${params.toString()}`);
        setItems(data.items || []);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setItems([]);
    } finally { setLoading(false); }
  }, [tab, filters, isAdmin]);

  useEffect(() => {
    api.get("/library/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/library/pending").then(({ data }) => setPendingCount((data.items || []).length)).catch(() => {});
    }
  }, [isAdmin, items]);

  const download = async (item) => {
    try {
      const token = localStorage.getItem("lexlink_token");
      const url = `${API}/library/items/${item.id}/file`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || "No fue posible descargar el archivo.");
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = item.file_name || "documento";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err.message || "Fallo la descarga.");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.title}" de la biblioteca?`)) return;
    try {
      await api.delete(`/library/items/${item.id}`);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const review = async (item, decision, notes) => {
    try {
      await api.post(`/library/items/${item.id}/review`, { decision, review_notes: notes || null });
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!user) {
    return (
      <div className="container-app py-16 max-w-2xl">
        <div className="panel p-8 text-center">
          <BookOpen className="mx-auto text-cyan-300" size={30}/>
          <h1 className="serif text-3xl mt-3">Biblioteca Jurídica Colega Link</h1>
          <p className="mt-3 text-muted-foreground">Ingresa a tu cuenta para acceder al repositorio colaborativo.</p>
          <Link to="/login?next=/app/biblioteca-juridica" className="btn-primary mt-6 inline-flex" data-testid="lib-login-cta">Ingresar</Link>
        </div>
      </div>
    );
  }

  if (!canBrowse) {
    return (
      <div className="container-app py-16 max-w-2xl" data-testid="lib-access-denied">
        <div className="panel p-8 text-center">
          <AlertTriangle className="mx-auto text-amber-300" size={26}/>
          <h1 className="serif text-2xl mt-3">Acceso restringido</h1>
          <p className="mt-3 text-muted-foreground">La Biblioteca Jurídica es un espacio para abogados y mediadores verificados.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-app py-10" data-testid="biblioteca-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-cyan-300 inline-flex items-center gap-2"><BookOpen size={12}/> Biblioteca colaborativa</div>
          <h1 className="serif text-4xl md:text-5xl mt-3 leading-[1]">Biblioteca <span className="brand-gradient-text">Jurídica</span></h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Documentos validados por abogados verificados: escritos, contratos, jurisprudencia, doctrina, códigos y apuntes.
            Cada envío pasa por revisión editorial antes de publicarse.
          </p>
        </div>
        {canUpload && (
          <button
            onClick={() => setTab("subir")}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="lib-btn-subir"
          >
            <Upload size={14}/> Subir documento
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10 pb-1" data-testid="lib-tabs">
        <TabBtn active={tab === "directorio"} onClick={() => setTab("directorio")} testid="tab-directorio">Directorio</TabBtn>
        {canUpload && <TabBtn active={tab === "subir"} onClick={() => setTab("subir")} testid="tab-subir">Subir</TabBtn>}
        {canUpload && <TabBtn active={tab === "mios"} onClick={() => setTab("mios")} testid="tab-mios">Mis envíos</TabBtn>}
        {isAdmin && (
          <TabBtn active={tab === "admin"} onClick={() => setTab("admin")} testid="tab-admin">
            Cola revisión {pendingCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 text-[10px]">{pendingCount}</span>}
          </TabBtn>
        )}
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-md border border-red-400/40 bg-red-400/10 text-sm text-red-200 inline-flex items-center gap-2" data-testid="lib-error">
          <AlertTriangle size={14}/> {error}
        </div>
      )}

      {/* Contenido según tab */}
      <div className="mt-6">
        {tab === "subir" ? (
          <UploadForm categories={categories} onDone={() => { setTab("mios"); load(); }} />
        ) : (
          <>
            {(tab === "directorio") && (
              <div className="panel p-4 flex flex-wrap gap-3 items-end mb-5" data-testid="lib-filters">
                <div className="grow min-w-[220px]">
                  <label className="text-xs label-eyebrow">Buscar</label>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-3.5 text-muted-foreground"/>
                    <input
                      className="field pl-9 mt-1 w-full"
                      placeholder="Título, descripción o autor…"
                      value={filters.q}
                      onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                      data-testid="lib-filter-q"
                    />
                  </div>
                </div>
                <div className="min-w-[220px]">
                  <label className="text-xs label-eyebrow">Categoría</label>
                  <select
                    className="field mt-1 w-full"
                    value={filters.category}
                    onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                    data-testid="lib-filter-category"
                  >
                    <option value="">Todas</option>
                    {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <button className="btn-ghost text-sm" onClick={load} data-testid="lib-apply-filters"><Filter size={13} className="mr-1"/> Aplicar</button>
              </div>
            )}

            {loading ? (
              <div className="panel p-10 text-center text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
                <Loader2 size={16} className="animate-spin"/> Cargando…
              </div>
            ) : items.length === 0 ? (
              <div className="panel p-10 text-center text-muted-foreground" data-testid="lib-empty">
                <FileText className="mx-auto mb-3" size={22}/>
                {tab === "admin"
                  ? "No hay documentos pendientes de revisión."
                  : tab === "mios"
                  ? "Aún no has subido documentos."
                  : "No hay documentos con estos filtros. Sé el primero en aportar."}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((it) => (
                  <LibraryCard
                    key={it.id}
                    item={it}
                    categories={categories}
                    isAdmin={isAdmin}
                    isMine={it.uploaded_by === user.id}
                    showAdminActions={tab === "admin"}
                    onDownload={() => download(it)}
                    onDelete={() => remove(it)}
                    onApprove={() => review(it, "aprobado")}
                    onReject={() => {
                      const notes = window.prompt("Motivo del rechazo (opcional):");
                      review(it, "rechazado", notes || undefined);
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-2 text-sm rounded-t-md transition-colors ${
        active ? "text-foreground border-b-2 border-cyan-400 -mb-[9px]" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function LibraryCard({ item, categories, isAdmin, isMine, showAdminActions, onDownload, onDelete, onApprove, onReject }) {
  const catLabel = categories.find((c) => c.key === item.category)?.label || item.category;
  const st = STATUS_META[item.status] || STATUS_META.pendiente_revision;
  const StatusIcon = st.icon;
  return (
    <div className="panel p-5 flex flex-col" data-testid={`lib-card-${item.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`pill text-[10px] inline-flex items-center gap-1 border ${st.cls}`} data-testid={`lib-status-${item.id}`}>
          <StatusIcon size={10}/> {st.label}
        </span>
        <span className="pill text-[10px] badge-cyan">{catLabel}</span>
      </div>
      <h3 className="serif text-lg mt-3 leading-snug line-clamp-2">{item.title}</h3>
      {item.description && <p className="mt-2 text-xs text-muted-foreground line-clamp-3">{item.description}</p>}
      <div className="mt-3 space-y-1 text-[11px] text-muted-foreground">
        <div className="inline-flex items-center gap-1"><User size={11}/> {item.uploader_name || "—"}</div>
        <div className="inline-flex items-center gap-2">
          <span className="inline-flex items-center gap-1"><FileText size={11}/> {item.file_name}</span>
          <span>· {humanSize(item.file_size)}</span>
        </div>
        {item.downloads > 0 && <div>· {item.downloads} descargas</div>}
        {item.review_notes && item.status === "rechazado" && (
          <div className="mt-1 p-2 rounded-md border border-red-400/25 bg-red-400/5 text-red-200 text-[11px]">
            <strong>Motivo:</strong> {item.review_notes}
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {item.status === "aprobado" && (
          <button className="btn-primary py-1.5 text-xs inline-flex items-center gap-1" onClick={onDownload} data-testid={`lib-download-${item.id}`}>
            <Download size={12}/> Descargar
          </button>
        )}
        {(isMine || isAdmin) && (
          <button className="btn-ghost py-1.5 text-xs inline-flex items-center gap-1 text-red-300 hover:text-red-200" onClick={onDelete} data-testid={`lib-delete-${item.id}`}>
            <Trash2 size={12}/> Eliminar
          </button>
        )}
        {showAdminActions && item.status === "pendiente_revision" && (
          <>
            <button className="btn-ghost py-1.5 text-xs inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200" onClick={onApprove} data-testid={`lib-approve-${item.id}`}>
              <CheckCircle2 size={12}/> Aprobar
            </button>
            <button className="btn-ghost py-1.5 text-xs inline-flex items-center gap-1 text-red-300 hover:text-red-200" onClick={onReject} data-testid={`lib-reject-${item.id}`}>
              <XCircle size={12}/> Rechazar
            </button>
            <button className="btn-ghost py-1.5 text-xs inline-flex items-center gap-1" onClick={onDownload} data-testid={`lib-preview-${item.id}`}>
              <Download size={12}/> Previsualizar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function UploadForm({ categories, onDone }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) { setError("Selecciona un archivo."); return; }
    if (file.size > 10 * 1024 * 1024) { setError("El archivo excede 10 MB."); return; }
    if (!category) { setError("Selecciona una categoría."); return; }

    setLoading(true);
    try {
      const form = new FormData();
      form.append("title", title.trim());
      form.append("description", description.trim());
      form.append("category", category);
      form.append("file", file);
      await api.post("/library/items", form, { headers: { "Content-Type": "multipart/form-data" } });
      setTitle(""); setDescription(""); setCategory(""); setFile(null);
      await onDone?.();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="panel p-6 max-w-2xl space-y-5" data-testid="lib-upload-form">
      <div className="inline-flex items-center gap-2 text-sm text-cyan-300"><ShieldCheck size={14}/> Envío revisado editorialmente</div>
      <div className="p-3 rounded-md border border-cyan-400/25 bg-cyan-400/5 text-xs text-cyan-100/90 flex items-start gap-2">
        <Info size={13} className="mt-0.5 shrink-0"/>
        <div>
          Los envíos son <strong>anónimos entre pares</strong> hasta la aprobación. No incluyas datos personales del cliente
          ni información sensible del expediente. Sube sólo material que tengas derecho a compartir.
        </div>
      </div>

      <div>
        <label className="text-xs label-eyebrow">Título</label>
        <input
          className="field mt-1 w-full" required minLength={3} maxLength={180}
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Ej: Modelo demanda de alimentos mayores"
          data-testid="lib-upload-title"
        />
      </div>

      <div>
        <label className="text-xs label-eyebrow">Descripción</label>
        <textarea
          className="field mt-1 w-full min-h-[100px]" maxLength={1200}
          value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Contexto de uso, jurisdicción, notas de aplicación…"
          data-testid="lib-upload-description"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs label-eyebrow">Categoría</label>
          <select
            className="field mt-1 w-full" required
            value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="lib-upload-category"
          >
            <option value="">Selecciona…</option>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs label-eyebrow">Archivo (PDF/Word/TXT, máx. 10 MB)</label>
          <input
            type="file" required
            accept=".pdf,.doc,.docx,.txt,.md,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mt-1 w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-white/10 file:text-foreground hover:file:bg-white/20"
            data-testid="lib-upload-file"
          />
          {file && <div className="mt-1 text-[11px] text-muted-foreground">{file.name} · {humanSize(file.size)}</div>}
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-md border border-red-400/40 bg-red-400/10 text-sm text-red-200 inline-flex items-center gap-2" data-testid="lib-upload-error">
          <AlertTriangle size={14}/> {error}
        </div>
      )}

      <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={loading} data-testid="lib-upload-submit">
        {loading ? <><Loader2 size={14} className="animate-spin"/> Subiendo…</> : <><Upload size={14}/> Enviar a revisión</>}
      </button>
    </form>
  );
}
